import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useContext } from "react";
import { FormattedPlural, IntlContext, defineMessages } from "react-intl";
import { useSelector } from "react-redux";

import {
  getFlightStreamData,
  getMarketingAirlinesFromFlightSegments,
} from "shared/components/Flights/FlightStream";
import { FlightStreams } from "shared/gol-api/types/flightSearch";
import {
  countriesWithUSFormat,
  dayMonthFormat,
  hourMinuteFormat,
} from "shared/lib/datetime";
import { FlightOptions } from "shared/lib/golObjectTypes/SearchFlightResponse/Response";
import { formattedMessageParams } from "shared/messages";

import FormattedMessage from "@components/UI/FormattedMessage";

import AirportTitle from "../Formatters/AirportTitle";
import FormatDuration from "../Formatters/FormatDuration";
import SimpleSelect from "../UI/SimpleSelect";
import { DropdownIndicator } from "./FlightStreamHeader";

const { flightDetailTransfer, flightDetailTransfers } = defineMessages({
  flightDetailTransfer: formattedMessageParams("FlightAdditionalInfo.transfer"),
  flightDetailTransfers: formattedMessageParams(
    "FlightAdditionalInfo.transfers"
  ),
});

const SingleValue = (props) => (
  <span style={{ display: "inline-flex", marginLeft: "5px" }}>
    <div className="flight-segment-type-icon" /> <span>{props.data.label}</span>
  </span>
);

const getAirlineImageProps = (
  airlineCode: string,
  oTransportCompanies: any
) => {
  const airline = oTransportCompanies[airlineCode];
  return {
    src: airline?.LogoUrl.$t || "",
    alt: airline?.Name.$t || "",
    title: airline?.Name.$t || "",
  };
};

export default function FlightSegment({
  flightStream,
  selectedOptionKey,
  changeSelectedStreamParts,
  index,
  toggleShowDetailStream,
  options,
  offerKey,
}: {
  flightStream: FlightStreams | FlightOptions;
  index: number;
  toggleShowDetailStream: () => void;
  changeSelectedStreamParts?: (streamIndex: number, value: string) => void;
  selectedOptionKey?: string;
  offerKey: number;
}) {
  const intl = useContext(IntlContext);
  const { oTransportCompanies, defaultCountry } = useSelector((state) => ({
    oTransportCompanies: state.search.oTransportCompanies,
    defaultCountry:
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      "",
  }));
  const {
    selectedFlightStream,
    timesForSelection,
    isMultipleStreamOptions,
    dateDeparture,
    dateArrival,
    firstSegment,
    lastSegment,
  } = getFlightStreamData({
    options,
    flightStream,
    selectedOptionKey,
  });

  const lastSegmentArrival = moment(lastSegment.ArrivalDateTime).format(
    dayMonthFormat(defaultCountry)
  );
  const differentDay =
    lastSegmentArrival !==
      moment(firstSegment.DepartureDateTime).format(
        dayMonthFormat(defaultCountry)
      ) && lastSegmentArrival;

  const marketingAirlines = getMarketingAirlinesFromFlightSegments(
    selectedFlightStream.FlightSegments.FlightSegment
  );

  return (
    <div className="flight-segment">
      <div className="flight-segment-city-time">
        <div className="flight-segment-city-from">
          <Tooltip
            placement="right"
            mouseEnterDelay={0}
            mouseLeaveDelay={0.1}
            trigger={["hover"]}
            overlay={
              <div className="flight-segment-tooltip">
                <AirportTitle airportCode={firstSegment.OriginAirport} />
              </div>
            }
            overlayClassName="tooltip-background"
          >
            <div className="flight-segment-city">
              {firstSegment.OriginAirport}
            </div>
          </Tooltip>
        </div>
        <div className="flight-segment-time-from">
          {isMultipleStreamOptions ? null : (
            <div className="flight-segment-type-icon" />
          )}
          {isMultipleStreamOptions ? (
            <SimpleSelect
              testId={`flight-time-select-${index}-${offerKey}`}
              styles={{
                control: (base) => ({
                  ...base,
                  width: countriesWithUSFormat.includes(
                    defaultCountry.toLowerCase()
                  )
                    ? "115px"
                    : "90px",
                  minHeight: "35px",
                  height: "35px",
                  borderRadius: "4px",
                  border: "1px solid #C0D9E8",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "none",
                }),

                valueContainer: (base) => ({ ...base, padding: 0 }),
              }}
              isSearchable={false}
              value={{
                value: selectedFlightStream,
                label: moment(selectedFlightStream.DepartureDateTime).format(
                  hourMinuteFormat(defaultCountry)
                ),
              }}
              options={timesForSelection.map((flightStreamOption) => ({
                value: flightStreamOption.Key,
                label: moment(flightStreamOption.DepartureDateTime).format(
                  hourMinuteFormat(defaultCountry)
                ),
              }))}
              clearIndicator={null}
              clearable={false}
              components={{
                DropdownIndicator,
                IndicatorSeparator: () => null,
                SingleValue,
              }}
              onChange={(e) => {
                changeSelectedStreamParts(index + 1, e.value);
              }}
            />
          ) : (
            <div className="flight-segment-time">
              {moment(dateDeparture).format(hourMinuteFormat(defaultCountry))}
            </div>
          )}
        </div>
      </div>

      <div className="flight-segment-middle">
        <div className="flight-segment-line" />
        <div
          className="flight-segment-transfers-duration pointer"
          onClick={toggleShowDetailStream}
          role="button"
        >
          <div className="flight-segment-duration">
            <FormatDuration
              showNothingIfNotDefined
              duration={selectedFlightStream.JourneyDuration}
            />
          </div>
          <span className="flight-segment-marks-transfers-description">
            {selectedFlightStream.numTransfers > 0 ? (
              <>
                {selectedFlightStream.numTransfers}{" "}
                <FormattedPlural
                  one={intl.formatMessage(flightDetailTransfer)}
                  other={intl.formatMessage(flightDetailTransfers)}
                  value={selectedFlightStream.numTransfers}
                />
              </>
            ) : (
              <FormattedMessage id="FlightAdditionalInfo.nonstop" />
            )}
          </span>
        </div>
        <div className="flight-segment-line" />
        <div className="flight-segment-next-day-warning">{differentDay}</div>
      </div>

      <div className="flight-segment-city-time">
        <div className="flight-segment-marks-to">
          <Tooltip
            placement="left"
            mouseEnterDelay={0}
            mouseLeaveDelay={0.1}
            trigger={["hover"]}
            overlay={
              <div className="flight-segment-tooltip">
                <AirportTitle airportCode={lastSegment.DestinationAirport} />
              </div>
            }
            overlayClassName="tooltip-background"
          >
            <div className="flight-segment-city">
              {lastSegment.DestinationAirport}
            </div>
          </Tooltip>
        </div>
        <div className="flight-segment-time">
          {moment(dateArrival).format(hourMinuteFormat(defaultCountry))}
        </div>
      </div>

      <div className="flight-segment-airline">
        {marketingAirlines.length === 1 ? (
          <div className="flight-segment-airline-logos flight-segment-airline-logos--single">
            <div className="flight-segment-airline-logo-height">
              <img
                alt="Airline logo"
                className="flight-segment-airline-logo flight-segment-airline-logo--large"
                {...getAirlineImageProps(
                  marketingAirlines[0],
                  oTransportCompanies
                )}
              />
            </div>
          </div>
        ) : marketingAirlines.length === 2 ? (
          <div className="flight-segment-airline-logos flight-segment-airline-logos--double">
            {marketingAirlines.map((marketingAirline: string) => (
              <div
                key={marketingAirline}
                className="flight-segment-airline-logo-height"
              >
                <img
                  alt="Airline logo"
                  className="flight-segment-airline-logo"
                  {...getAirlineImageProps(
                    marketingAirline,
                    oTransportCompanies
                  )}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flight-segment-airline-logos flight-segment-airline-logos--multiple">
            <div className="flight-segment-airline-logo-height">
              <div className="flight-segment-airline-placeholder">
                <img
                  alt="Multiple airlines"
                  src="/static/images/plane-logo.svg"
                  className="flight-segment-airline-plane-icon"
                />
                <span className="flight-segment-airline-count">
                  {marketingAirlines.length}x
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
